import React, { useEffect, useState, useCallback } from "react";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import { fmtDateIT } from "@/lib/format";
import { toast } from "sonner";
import { Loader2, Calendar as CalIcon, Info } from "lucide-react";
import { Popover, PopoverTrigger, PopoverContent } from "@/components/ui/popover";
import { Calendar as CalendarPicker } from "@/components/ui/calendar";
import { it as itLocale } from "date-fns/locale";
import { CatBadge } from "./RejectedTab";
import { useFromState } from "@/lib/navContext";
import { useScrollRestore } from "@/hooks/useScrollRestore";

const GROUP_STYLES = {
  pronta: ["Pronta per rivalutazione", "border-emerald-300 bg-emerald-50/40"],
  dati_mancanti: ["Dati da recuperare", "border-rose-300 bg-rose-50/30"],
  in_attesa: ["In attesa", "border-slate-200 bg-white"],
  recall_esistente: ["Recall già fissato", "border-sky-200 bg-sky-50/30"],
  appuntamento_fissato: ["Appuntamento fissato", "border-sky-200 bg-sky-50/30"],
  gia_ripresentata: ["Già ripresentata", "border-slate-200 bg-slate-50"],
  esclusa: ["Esclusa (divieto)", "border-slate-300 bg-slate-100/60"],
};

function SetRecall({ g, onSaved }) {
  const [open, setOpen] = useState(false);
  const save = async (d) => {
    if (!d) return;
    const iso = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
    try {
      await api.patch(`/clients/${g.client_id}`, { data: iso });
      setOpen(false);
      toast.success(`Recall impostato al ${fmtDateIT(iso)} per ${g.cliente}`);
      onSaved();
    } catch (e) { toast.error(e?.response?.data?.detail || "Salvataggio non riuscito: nessuna modifica applicata"); }
  };
  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button data-testid={`reev-set-recall-${g.client_id}`}
          className="px-2.5 py-1.5 text-xs bg-[#0A192F] text-white rounded-[3px] hover:opacity-90 inline-flex items-center gap-1.5 min-h-[32px]">
          <CalIcon className="w-3.5 h-3.5" /> Imposta recall
        </button>
      </PopoverTrigger>
      <PopoverContent className="w-auto p-0" align="start">
        <div className="px-3 py-2 text-[11px] text-slate-500 border-b border-slate-100 max-w-[240px]">Un solo richiamo per il cliente, anche con più pratiche. La rivalutazione della pratica resta separata dal recall.</div>
        <CalendarPicker mode="single" locale={itLocale}
          defaultMonth={g.prossima_rivalutazione ? new Date(`${g.prossima_rivalutazione}T00:00:00`) : undefined}
          onSelect={save} />
      </PopoverContent>
    </Popover>
  );
}

export default function ReevaluateTab() {
  const [d, setD] = useState(null);
  const [soloPersonali, setSoloPersonali] = useState(false);
  const fromState = useFromState();

  const load = useCallback(async () => {
    const r = await api.get("/portfolio-active/reevaluate", { params: soloPersonali ? { solo_personali: true } : {} });
    setD(r.data);
  }, [soloPersonali]);
  useEffect(() => { load().catch(() => setD({ items: [] })); }, [load]);
  useScrollRestore(!!d);

  if (!d) return <div className="py-10 flex justify-center"><Loader2 className="w-5 h-5 animate-spin text-slate-400" /></div>;

  return (
    <div className="space-y-3" data-testid="pa-reevaluate">
      <div className="text-xs bg-slate-50 border border-slate-200 rounded-[3px] p-2.5 text-slate-600 flex items-start gap-1.5">
        <Info className="w-3.5 h-3.5 mt-0.5 shrink-0" /> {d.nota}
      </div>
      <label className="inline-flex items-center gap-1.5 text-xs text-slate-700">
        <input type="checkbox" checked={soloPersonali} onChange={(e) => setSoloPersonali(e.target.checked)} data-testid="reev-solo-personali" /> Solo clienti personali
      </label>

      <div className="space-y-2">
        {(d.items || []).map((g) => {
          const [glabel, gcls] = GROUP_STYLES[g.stato_gruppo] || [g.stato_gruppo, "border-slate-200 bg-white"];
          return (
            <div key={g.client_id} className={`border rounded-[3px] p-3 ${gcls}`} data-testid={`reev-group-${g.client_id}`}>
              <div className="flex flex-wrap items-center gap-2">
                <Link to={`/clienti/${g.client_id}`} state={fromState} className="font-semibold text-sm text-slate-900 hover:text-[#B7955B]">{g.cliente}</Link>
                {g.personale && <span className="text-[10px] px-1.5 py-0.5 rounded-[3px] bg-[#35008B] text-white">personale</span>}
                <span className="text-[10px] px-1.5 py-0.5 rounded-[3px] bg-white border border-slate-300 text-slate-700">{glabel}</span>
                {g.pp_esclusa && <span className="text-[10px] px-1.5 py-0.5 rounded-[3px] bg-rose-100 text-rose-800 border border-rose-200">no riproposta PP</span>}
                <span className="text-[11px] text-slate-500">{g.n_pratiche_rifiutate} pratica/e rifiutata/e</span>
                <div className="ml-auto flex items-center gap-2">
                  {g.recall_attuale && <span className="text-[11px] text-slate-600 tabular-nums">recall attuale: <b>{fmtDateIT(g.recall_attuale)}</b></span>}
                  {g.puo_impostare_recall && <SetRecall g={g} onSaved={load} />}
                </div>
              </div>
              <div className="mt-1.5 flex flex-wrap gap-1.5">
                {g.pratiche.map((p) => (
                  <Link key={p.id} to={`/pipeline?mk=${p.monthKey}&focus=${p.id}`} state={fromState}
                    className="text-[11px] px-1.5 py-1 rounded-[3px] bg-white border border-slate-200 hover:border-[#B7955B] inline-flex items-center gap-1.5">
                    {p.prodotto} {p.monthKey} <CatBadge cat={p.categoria} />
                    {p.rivalutazione && <span className="tabular-nums text-slate-500">riv. {fmtDateIT(p.rivalutazione)}</span>}
                    {p.esito_da_recuperare && <span className="text-rose-600">data rifiuto mancante</span>}
                  </Link>
                ))}
              </div>
              <div className="mt-1.5 text-xs text-slate-700" data-testid={`reev-azione-${g.client_id}`}>
                <span className="text-slate-500">Azione suggerita:</span> <b>{g.azione_suggerita}</b>
                {g.aggiornamenti_dopo_rifiuto > 0 && <span className="text-amber-700"> · {g.aggiornamenti_dopo_rifiuto} aggiornamenti dopo il rifiuto: leggerli prima di contattare</span>}
              </div>
            </div>
          );
        })}
        {(d.items || []).length === 0 && <div className="py-8 text-center text-slate-400 text-sm bg-white border border-slate-200 rounded-[3px]">Nessun rifiuto da rivalutare nei filtri scelti</div>}
      </div>

      {d.senza_anagrafica?.length > 0 && (
        <div className="text-xs bg-amber-50 border border-amber-200 rounded-[3px] p-2.5 text-amber-800" data-testid="reev-unlinked">
          {d.senza_anagrafica.length} pratiche rifiutate senza anagrafica collegata: confermare il collegamento in Pratiche del Mese (mai automatico per nome).
        </div>
      )}
    </div>
  );
}
